import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Checkbox,
  Tooltip,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useTranslation } from 'react-i18next';
import CardImage from './CardImage';
import FreshnessBadge from './FreshnessBadge';
import FinMapleScoreBadge from '../FinMapleScore/FinMapleScoreBadge';
import { parseFee, fmtCurrency, fmtPercent, getRewardType } from './helpers';

/* ── i18n labels ── */
const L = {
  es: {
    cashback: 'Cash back', points: 'Puntos', miles: 'Millas', rewards: 'Recompensas',
    noCards: 'No se encontraron tarjetas con estos filtros.',
    adjustFilters: 'Intenta ajustar los filtros para ver más resultados.',
    viewDetails: 'Ver detalles',
    addCompare: 'Agregar a comparación',
    removeCompare: 'Quitar de comparación',
    compareAria: 'Comparar',
    summaryNewcomerFree: 'Mejor para recién llegados sin historial',
    summaryNewcomer: 'Diseñada para recién llegados en Canadá',
    summaryNoFee: 'Sin cuota anual · Ideal para comenzar',
    summaryCashback: 'Cash back en compras del día a día',
    summaryTravel: 'Recompensas de viaje y puntos',
    summaryDefault: 'Opción competitiva en tasas y beneficios',
  },
  en: {
    cashback: 'Cash back', points: 'Points', miles: 'Miles', rewards: 'Rewards',
    noCards: 'No cards found with these filters.',
    adjustFilters: 'Try adjusting the filters to see more results.',
    viewDetails: 'View details',
    addCompare: 'Add to comparison',
    removeCompare: 'Remove from comparison',
    compareAria: 'Compare',
    summaryNewcomerFree: 'Best for newcomers with no credit history',
    summaryNewcomer: 'Designed for newcomers to Canada',
    summaryNoFee: 'No annual fee · Great to get started',
    summaryCashback: 'Cash back on everyday purchases',
    summaryTravel: 'Travel rewards and points',
    summaryDefault: 'Competitive rates and benefits',
  },
  fr: {
    cashback: 'Remises', points: 'Points', miles: 'Milles', rewards: 'Récompenses',
    noCards: 'Aucune carte trouvée avec ces filtres.',
    adjustFilters: 'Essayez d\'ajuster les filtres pour voir plus de résultats.',
    viewDetails: 'Voir détails',
    addCompare: 'Ajouter à la comparaison',
    removeCompare: 'Retirer de la comparaison',
    compareAria: 'Comparer',
    summaryNewcomerFree: 'Idéale pour les nouveaux arrivants sans historique',
    summaryNewcomer: 'Conçue pour les nouveaux arrivants au Canada',
    summaryNoFee: 'Sans frais annuels · Idéale pour commencer',
    summaryCashback: 'Remises en argent sur les achats quotidiens',
    summaryTravel: 'Récompenses voyage et points',
    summaryDefault: 'Taux et avantages compétitifs',
  },
};

/* Quick summary generator */
const getQuickSummary = (card, l) => {
  const fee = parseFee(card.annual_fee);
  if (card.newcomer_friendly && fee === 0) return l.summaryNewcomerFree;
  if (card.newcomer_friendly) return l.summaryNewcomer;
  if (fee === 0) return l.summaryNoFee;
  const rt = getRewardType(card);
  if (rt === 'cashback') return l.summaryCashback;
  if (rt === 'miles' || rt === 'points') return l.summaryTravel;
  return l.summaryDefault;
};

/* ── Standardized tag chip ─────────────────── */
const TagChip = ({ type, label, icon }) => {
  const styles = {
    fee: { bgcolor: '#1E3A5F', color: '#fff' },
    fee_paid: { bgcolor: 'rgba(0,0,0,0.04)', color: '#5F6B7A' },
    rate: { bgcolor: 'rgba(0,0,0,0.04)', color: '#5F6B7A' },
    newcomer: { bgcolor: 'rgba(46,125,50,0.08)', color: '#2E7D32', '& .MuiChip-icon': { color: '#2E7D32' } },
    reward: { bgcolor: 'rgba(249,168,37,0.12)', color: '#7D5A00' },
  };

  return (
    <Chip
      label={label}
      icon={icon}
      size="small"
      sx={{ fontWeight: 600, fontSize: '0.875rem', ...styles[type] }}
    />
  );
};

/* ── Circular rank badge ─────────────────────── */
const RankBadge = ({ rank, size = 32 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: rank <= 3 ? 'rgba(249,168,37,0.10)' : 'grey.100',
      border: '1.5px solid',
      borderColor: rank <= 3 ? 'rgba(249,168,37,0.4)' : 'divider',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontFeatureSettings: "'tnum'",
        fontSize: size >= 32 ? '0.875rem' : '0.75rem',
        color: rank <= 3 ? '#C68A00' : 'text.disabled',
        lineHeight: 1,
      }}
    >
      {rank}
    </Typography>
  </Box>
);

const CardRanked = ({ cards, selectedForCompare, onToggleCompare, onViewDetail, disclosure = {}, pageOffset = 0 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const l = L[lang] || L.es;

  const REWARD_TYPE_LABELS = {
    cashback: l.cashback, points: l.points, miles: l.miles, other: l.rewards, none: null,
  };

  if (!cards || cards.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          {l.noCards}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {l.adjustFilters}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {cards.map((card, idx) => {
        const isSelected = selectedForCompare.some((c) => c.id === card.id);
        const fee = parseFee(card.annual_fee);
        const rewardType = getRewardType(card);
        const rewardLabel = REWARD_TYPE_LABELS[rewardType];
        const globalRank = pageOffset + idx + 1;
        const summary = getQuickSummary(card, l);

        if (isMobile) {
          /* ── Mobile: stacked layout ──────────── */
          return (
            <Paper
              key={card.id}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                  transform: 'translateY(-1px)',
                },
                '@keyframes fadeInRow': {
                  from: { opacity: 0, transform: 'translateY(6px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: `fadeInRow 0.25s ease-out ${idx * 0.04}s both`,
              }}
            >
              {/* Top: rank + image + name */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'flex-start' }}>
                <RankBadge rank={globalRank} size={28} />
                <CardImage card={card} width={80} height={52} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {card.bank}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
                  >
                    {card.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FinMapleScoreBadge card={card} />
                    {card.v2?.freshness && <FreshnessBadge status={card.v2.freshness} />}
                  </Box>
                </Box>
              </Box>

              {/* Quick summary */}
              <Typography sx={{ fontSize: '0.875rem', color: '#5F6B7A', mb: 1, fontStyle: 'italic' }}>
                {summary}
              </Typography>

              {/* Welcome bonus */}
              {card.v2?.welcome_bonus && (
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, mb: 1, display: 'block' }}>
                  {card.v2.welcome_bonus.headline}
                </Typography>
              )}

              {/* Middle: stats */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                <TagChip type={fee === 0 ? 'fee' : 'fee_paid'} label={fmtCurrency(card.annual_fee, lang)} />
                <TagChip type="rate" label={fmtPercent(card.purchase_rate)} />
                {card.newcomer_friendly && (
                  <TagChip type="newcomer" label="Newcomers" icon={<VerifiedUserIcon sx={{ fontSize: '0.875rem' }} />} />
                )}
                {rewardLabel && <TagChip type="reward" label={rewardLabel} />}
              </Box>

              {/* Bottom: actions */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onViewDetail(card)}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'divider',
                    color: 'text.primary',
                    borderRadius: '8px',
                  }}
                >
                  {l.viewDetails}
                </Button>
                <Tooltip title={isSelected ? l.removeCompare : l.addCompare} arrow>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleCompare(card)}
                    disabled={!isSelected && selectedForCompare.length >= 4}
                    size="small"
                    sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                  />
                </Tooltip>
              </Box>

              {/* Disclosure microline */}
              {card.v2?.commercial?.disclosure_key && disclosure[card.v2.commercial.disclosure_key] && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                  {disclosure[card.v2.commercial.disclosure_key]}
                </Typography>
              )}
            </Paper>
          );
        }

        /* ── Desktop: horizontal row ──────────── */
        return (
          <Paper
            key={card.id}
            elevation={0}
            sx={{
              px: 3,
              py: 2.5,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)',
                borderColor: '#E6E9EE',
              },
              '@keyframes fadeInRow': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              animation: `fadeInRow 0.25s ease-out ${idx * 0.04}s both`,
            }}
          >
            {/* Rank */}
            <RankBadge rank={globalRank} />

            {/* Image */}
            <CardImage card={card} width={100} height={64} sx={{ flexShrink: 0 }} />

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {card.bank}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
              >
                {card.name}
              </Typography>
              {/* Quick summary */}
              <Typography sx={{ fontSize: '0.875rem', color: '#5F6B7A', fontStyle: 'italic', mt: 0.25 }}>
                {summary}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FinMapleScoreBadge card={card} />
                {card.v2?.freshness && <FreshnessBadge status={card.v2.freshness} />}
              </Box>
              {card.v2?.welcome_bonus && (
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {card.v2.welcome_bonus.headline}
                </Typography>
              )}
            </Box>

            {/* Stats - standardized tags */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0 }}>
              <TagChip type={fee === 0 ? 'fee' : 'fee_paid'} label={fmtCurrency(card.annual_fee, lang)} />
              <TagChip type="rate" label={fmtPercent(card.purchase_rate)} />
              {card.newcomer_friendly && (
                <TagChip type="newcomer" label="Newcomers" icon={<VerifiedUserIcon sx={{ fontSize: '0.875rem' }} />} />
              )}
              {rewardLabel && <TagChip type="reward" label={rewardLabel} />}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Tooltip title={isSelected ? l.removeCompare : l.addCompare} arrow>
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggleCompare(card)}
                  disabled={!isSelected && selectedForCompare.length >= 4}
                  size="small"
                  aria-label={`${l.compareAria} ${card.name}`}
                  sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                />
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                onClick={() => onViewDetail(card)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.primary',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                }}
              >
                {l.viewDetails}
              </Button>
            </Box>

            {/* Disclosure microline */}
            {card.v2?.commercial?.disclosure_key && disclosure[card.v2.commercial.disclosure_key] && (
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem', flexBasis: '100%' }}>
                {disclosure[card.v2.commercial.disclosure_key]}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default CardRanked;
